import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';

import { type CurrentError, type FlowListName } from '@/components/Wizard';
import { trackValidationError } from '@/lib/gtm';

export type ErrorInfo = Omit<CurrentError, 'lastFlow'>;

export type FieldErrorInfo = { fieldErrors: Record<string, string> };

export type GetErrorInfo<E = unknown> = (error: E) => ErrorInfo | FieldErrorInfo;

export type Loading = {
  action: () => Promise<unknown>;
  lastFlow: FlowListName;
  label?: string;
  title?: string;
  subtitle?: string;
  error: GetErrorInfo;
  onCompleted?: () => void | Promise<void>;
};

type TriggerError = (error: Omit<CurrentError, 'lastFlow'> & { lastFlow?: FlowListName }) => void;

type ApplyFieldErrors = (errors: Record<string, string>) => void;

export const useLoading = (
  flowSetter: Dispatch<SetStateAction<FlowListName>>,
  triggerError: TriggerError,
  applyFieldErrors: ApplyFieldErrors
) => {
  const [loading, setLoading] = useState<Loading>();

  const triggerLoading = useCallback(
    (value: Omit<Loading, 'lastFlow'>) => {
      flowSetter(lastFlow => {
        setLoading({ ...value, lastFlow });

        return 'loading';
      });
    },
    [flowSetter]
  );

  const clearLoading = useCallback(() => {
    setLoading(undefined);
  }, []);

  useEffect(() => {
    if (!loading) {
      return;
    }

    const target = loading;
    let cancelled = false;

    void (async () => {
      try {
        await target.action();

        if (cancelled) {
          return;
        }

        flowSetter(target.lastFlow);
      } catch (error) {
        if (cancelled) {
          return;
        }

        const errorInfo = target.error(error);

        if ('fieldErrors' in errorInfo) {
          applyFieldErrors(errorInfo.fieldErrors);
          flowSetter(target.lastFlow);
          setLoading(undefined);
          return;
        }

        trackValidationError(error, errorInfo.subtitle);
        triggerError({ ...errorInfo, lastFlow: target.lastFlow });
        setLoading(undefined);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, flowSetter, triggerError, applyFieldErrors]);

  return { loading, triggerLoading, clearLoading };
};
